/**
 * intentContextFormat (INTENT-HUMANIZE-1, William 2026-07-04) — turn the
 * machine-readable project-intent summary into reader-facing prose at the
 * DISPLAY layer.
 *
 * `summarizeProjectIntent` (ProjectIntentForm) emits a compact editor string
 * that the LLM prompt relies on, e.g.:
 *   "colocation · Phased 300 MW (4 phases) · OR: Grid import, 1803 Electric
 *    Cooperative (Moss Bluff 230/69 kV) grid → AND: Grid import + Recip engines,
 *    1803 Electric Cooperative (Moss Bluff 230/69 kV) grid · 2027-12-31 → 2029-12-31"
 * William saw that verbatim in the Key Findings "INTENT CONTEXT" block and the
 * section-01 body — raw "OR:/AND:" editor prefixes, a utility name repeated per
 * phase, and ISO dates. We do NOT touch the machine summary (the prompt uses
 * it); instead every reader surface routes it through here, which also heals
 * already-persisted (legacy) reports without regeneration.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** "2027-12-31" → "Dec 2027"; null when not an ISO date. */
function isoToMonthYear(iso: string): string | null {
  if (/^\d{4}$/.test(iso.trim())) return iso.trim();
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(iso.trim());
  if (!m) return null;
  const mo = Number(m[2]);
  if (mo < 1 || mo > 12) return null;
  return `${MONTHS[mo - 1]} ${m[1]}`;
}

/** The longest trailing ", … grid" clause of a phase (the utility anchor that
 *  repeats across phases), else null. */
function trailingGridClause(phase: string): string | null {
  const m = /,\s*[^,]+?\bgrid$/i.exec(phase);
  return m ? m[0] : null;
}

/**
 * Humanize ONE ' · '-delimited segment of the intent summary. Reader-facing:
 *   - ISO date range → "in service Dec 2027 to Dec 2029";
 *   - a phase mix carrying "OR:"/"AND:" editor prefixes → drop the prefixes,
 *     join phases with " then " (phase progression), and de-duplicate the
 *     utility clause repeated on later phases;
 *   - anything else passes through untouched.
 */
import type { ProjectIntent } from '../types/siteRag';
import { TIMELINE_BUCKET_LABEL, effectivePhases } from './projectIntent';

export function humanizeIntentSegment(seg: string, intent?: ProjectIntent | null): string {
  const s = seg.trim();
  if (!s) return s;

  const pm = /^Phase 1 COD (\d{4}-\d{2}-\d{2})\s*(?:→|->|;)\s*(.+)$/i.exec(s);
  if (pm) {
    const a = isoToMonthYear(pm[1]);
    if (a) return `Phase 1 COD ${a}; ${pm[2].trim()}`;
  }

  const dm = /^(\d{4}-\d{2}-\d{2})\s*(?:→|->)\s*(\d{4}-\d{2}-\d{2})$/.exec(s);
  if (dm) {
    const a = isoToMonthYear(dm[1]);
    const b = isoToMonthYear(dm[2]);
    if (a && b) {
      if (dm[1] === dm[2]) {
        if (intent) {
          const view = effectivePhases(intent);
          if (view.isPhased && view.phaseCount > 1 && !view.ultimateInServiceDate && view.ultimateCodTimeline) {
            const finalPhaseLabel = view.phases[view.phases.length - 1]?.label || `Phase ${view.phaseCount}`;
            return `Phase 1 COD ${a}; ${finalPhaseLabel} ${TIMELINE_BUCKET_LABEL[view.ultimateCodTimeline]} (planning target, not a COD)`;
          }
        }
        return `Phase 1 COD ${a} (subsequent phase schedule is a planning target, not a COD)`;
      }
      return `in service ${a} to ${b}`;
    }
  }

  const hasMixPrefix = /(^|\s)(OR:|AND:)\s/i.test(s);
  if (hasMixPrefix) {
    const phases = s.split(/\s*(?:→|->)\s*/).map((p) => p.replace(/^\s*(OR:|AND:)\s*/i, '').trim()).filter(Boolean);
    if (phases.length > 0) {
      const anchor = trailingGridClause(phases[0]);
      const deduped = phases.map((p, i) => {
        if (i > 0 && anchor && p.endsWith(anchor)) {
          const trimmed = p.slice(0, p.length - anchor.length).trim();
          return trimmed.length > 0 ? trimmed : p;
        }
        return p;
      });
      return deduped.join(' then ');
    }
  }

  return s;
}

/**
 * Split the machine summary into humanized reader segments (drops any leading
 * "Evaluated in context of:" and trailing period the caller may have added).
 */
export function humanizeIntentSegments(raw: string, intent?: ProjectIntent | null): string[] {
  const cleaned = raw.trim().replace(/^Evaluated in context of:\s*/i, '').replace(/\.\s*$/, '');
  if (!cleaned) return [];
  return cleaned.split(' · ').map((seg) => humanizeIntentSegment(seg, intent)).filter((s) => s.length > 0);
}

/** The humanized summary as one ' · '-joined reader string. */
export function humanizeIntentSummary(raw: string | null | undefined, intent?: ProjectIntent | null): string {
  if (!raw) return '';
  return humanizeIntentSegments(raw, intent).join(' · ');
}
